import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Package, AlertCircle, CheckCircle, Clock, RefreshCw, Search, UserPlus, ChevronRight, Flag, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const DispatchSupervisor = () => {
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('pickers');
  
  // Staff list
  const [staffList, setStaffList] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  
  // Available pickers
  // 2026-05-24 — SFG pool split into Dhoti vs Combo. Each card holds
  // its own selection state; legacy unified `SFG` state has been
  // removed.
  const [sfgDhotiPickers, setSfgDhotiPickers] = useState([]);
  const [sfgComboPickers, setSfgComboPickers] = useState([]);
  const [selectedSfgDhotiPickers, setSelectedSfgDhotiPickers] = useState([]);
  const [selectedSfgComboPickers, setSelectedSfgComboPickers] = useState([]);
  const [fgPickers, setFgPickers] = useState([]);
  const [piecesProcessingStaff, setPiecesProcessingStaff] = useState([]);
  const [comboProcessingStaff, setComboProcessingStaff] = useState([]);
  const [packers, setPackers] = useState([]);
  const [selectedFgPickers, setSelectedFgPickers] = useState([]);
  const [selectedPiecesProcessingStaff, setSelectedPiecesProcessingStaff] = useState([]);
  const [selectedComboProcessingStaff, setSelectedComboProcessingStaff] = useState([]);
  const [selectedPackers, setSelectedPackers] = useState([]);
  
  // Pieces processing settings
  const [maxPiecesPerStaff, setMaxPiecesPerStaff] = useState(100);
  const [maxPiecesPerStaffCombo, setMaxPiecesPerStaffCombo] = useState(100);
  
  // Pending slips
  const [pendingSlips, setPendingSlips] = useState([]);
  const [isLoadingSlips, setIsLoadingSlips] = useState(false);
  
  // Priority dialog
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [newPriority, setNewPriority] = useState('3');
  
  // Assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedStaffForAssign, setSelectedStaffForAssign] = useState('');
  
  // Picker stats
  const [pickerStats, setPickerStats] = useState([]);

  // Packer Groups (persistent, reusable — credited equally at packing)
  const [packerGroups, setPackerGroups] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupMemberIds, setGroupMemberIds] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupStaffSearch, setGroupStaffSearch] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);

  // Load data on mount
  useEffect(() => {
    fetchStaffList();
    fetchAvailablePickers();
    fetchPendingSlips();
    fetchPickerStats();
    fetchPiecesProcessingSettings();
    fetchPackerGroups();
  }, []);

  const fetchPackerGroups = async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/packer-groups');
      setPackerGroups(response.data.groups || []);
    } catch (error) {
      console.error('Failed to load packer groups:', error);
    }
  };

  const resetGroupForm = () => {
    setEditingGroupId(null);
    setGroupName('');
    setGroupMemberIds([]);
    setGroupStaffSearch('');
  };

  const toggleGroupMember = (staff) => {
    setGroupMemberIds(prev =>
      prev.some(m => m.staff_id === staff.staff_id)
        ? prev.filter(m => m.staff_id !== staff.staff_id)
        : [...prev, { staff_id: staff.staff_id, staff_name: staff.staff_name }]
    );
  };

  const handleEditGroup = (group) => {
    setEditingGroupId(group.group_id);
    setGroupName(group.group_name);
    setGroupMemberIds((group.members || []).map(m => ({ staff_id: m.staff_id, staff_name: m.staff_name })));
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) { toast.error('Enter a group name'); return; }
    if (groupMemberIds.length === 0) { toast.error('Add at least one member'); return; }
    setSavingGroup(true);
    try {
      const payload = { group_name: groupName.trim(), members: groupMemberIds };
      if (editingGroupId) {
        await apiClient.put(`/wms/dispatch/packer-groups/${editingGroupId}`, payload);
        toast.success('Group updated');
      } else {
        await apiClient.post('/wms/dispatch/packer-groups', payload);
        toast.success('Group created');
      }
      resetGroupForm();
      fetchPackerGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save group');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete group "${group.group_name}"?`)) return;
    try {
      await apiClient.delete(`/wms/dispatch/packer-groups/${group.group_id}`);
      toast.success('Group deleted');
      if (editingGroupId === group.group_id) resetGroupForm();
      fetchPackerGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  const fetchStaffList = async (search = '') => {
    setIsLoadingStaff(true);
    try {
      const url = search 
        ? `/wms/dispatch/staff-list?search=${encodeURIComponent(search)}`
        : '/wms/dispatch/staff-list';
      const response = await apiClient.get(url);
      setStaffList(response.data.staff || []);
    } catch (error) {
      toast.error('Failed to load staff list');
    } finally {
      setIsLoadingStaff(false);
    }
  };

  const fetchAvailablePickers = async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/available-pickers');
      const availability = response.data.availability || {};
      
      if (availability.SFG_DHOTI) {
        setSfgDhotiPickers(availability.SFG_DHOTI.pickers || []);
        setSelectedSfgDhotiPickers(availability.SFG_DHOTI.picker_ids || []);
      } else if (availability.SFG) {
        // 2026-05-24 — Day-1 rollout fallback: pre-fill Dhoti pool from
        // the legacy unified SFG saved list so we don't lose history.
        setSfgDhotiPickers(availability.SFG.pickers || []);
        setSelectedSfgDhotiPickers(availability.SFG.picker_ids || []);
      }
      if (availability.SFG_COMBO) {
        setSfgComboPickers(availability.SFG_COMBO.pickers || []);
        setSelectedSfgComboPickers(availability.SFG_COMBO.picker_ids || []);
      }
      if (availability.FG) {
        setFgPickers(availability.FG.pickers || []);
        setSelectedFgPickers(availability.FG.picker_ids || []);
      }
      if (availability.PIECES_PROCESSING) {
        setPiecesProcessingStaff(availability.PIECES_PROCESSING.pickers || []);
        setSelectedPiecesProcessingStaff(availability.PIECES_PROCESSING.picker_ids || []);
      }
      if (availability.PIECES_PROCESSING_COMBO) {
        setComboProcessingStaff(availability.PIECES_PROCESSING_COMBO.pickers || []);
        setSelectedComboProcessingStaff(availability.PIECES_PROCESSING_COMBO.picker_ids || []);
      }
      if (availability.PACKER) {
        setPackers(availability.PACKER.pickers || []);
        setSelectedPackers(availability.PACKER.picker_ids || []);
      }
    } catch (error) {
      console.error('Failed to load available pickers:', error);
    }
  };

  const fetchPiecesProcessingSettings = async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/pieces-processing/settings');
      setMaxPiecesPerStaff(response.data.max_pieces_per_staff || 100);
      setMaxPiecesPerStaffCombo(response.data.max_pieces_per_staff_combo || 100);
    } catch (error) {
      console.error('Failed to load pieces processing settings:', error);
    }
  };

  const fetchPendingSlips = async () => {
    setIsLoadingSlips(true);
    try {
      const response = await apiClient.get('/wms/dispatch/pending-slips');
      setPendingSlips(response.data.slips || []);
    } catch (error) {
      toast.error('Failed to load pending slips');
    } finally {
      setIsLoadingSlips(false);
    }
  };

  const fetchPickerStats = async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/picker-stats');
      setPickerStats(response.data.stats || []);
    } catch (error) {
      console.error('Failed to load picker stats:', error);
    }
  };

  const handleSearchStaff = () => {
    fetchStaffList(staffSearch);
  };

  const handleTogglePicker = (staffId, type) => {
    if (type === 'SFG_DHOTI') {
      setSelectedSfgDhotiPickers(prev =>
        prev.includes(staffId)
          ? prev.filter(id => id !== staffId)
          : [...prev, staffId]
      );
    } else if (type === 'SFG_COMBO') {
      setSelectedSfgComboPickers(prev =>
        prev.includes(staffId)
          ? prev.filter(id => id !== staffId)
          : [...prev, staffId]
      );
    } else if (type === 'FG') {
      setSelectedFgPickers(prev => 
        prev.includes(staffId) 
          ? prev.filter(id => id !== staffId)
          : [...prev, staffId]
      );
    } else if (type === 'PIECES_PROCESSING') {
      setSelectedPiecesProcessingStaff(prev => 
        prev.includes(staffId) 
          ? prev.filter(id => id !== staffId)
          : [...prev, staffId]
      );
    } else if (type === 'PIECES_PROCESSING_COMBO') {
      setSelectedComboProcessingStaff(prev => 
        prev.includes(staffId) 
          ? prev.filter(id => id !== staffId)
          : [...prev, staffId]
      );
    } else if (type === 'PACKER') {
      setSelectedPackers(prev => 
        prev.includes(staffId) 
          ? prev.filter(id => id !== staffId)
          : [...prev, staffId]
      );
    }
  };

  const handleSavePickers = async (type) => {
    try {
      let pickerIds;
      if (type === 'SFG_DHOTI') {
        pickerIds = selectedSfgDhotiPickers;
      } else if (type === 'SFG_COMBO') {
        pickerIds = selectedSfgComboPickers;
      } else if (type === 'FG') {
        pickerIds = selectedFgPickers;
      } else if (type === 'PIECES_PROCESSING') {
        pickerIds = selectedPiecesProcessingStaff;
      } else if (type === 'PIECES_PROCESSING_COMBO') {
        pickerIds = selectedComboProcessingStaff;
      } else if (type === 'PACKER') {
        pickerIds = selectedPackers;
      }

      await apiClient.post('/wms/dispatch/set-available-pickers', {
        picker_type: type,
        staff_ids: pickerIds
      });
      const label = type === 'SFG_DHOTI' ? 'Dhoti Pickers' :
                     type === 'SFG_COMBO' ? 'Combo Pickers' :
                     type === 'PIECES_PROCESSING' ? 'Regular Processing' :
                     type === 'PIECES_PROCESSING_COMBO' ? 'Combo Processing' :
                     type === 'PACKER' ? 'Packers' : type;
      toast.success(`${label} staff updated successfully`);
      fetchAvailablePickers();
      fetchPickerStats();
    } catch (error) {
      toast.error('Failed to update staff');
    }
  };

  const handleSaveMaxPieces = async (processingType = 'regular') => {
    try {
      const value = processingType === 'combo' ? maxPiecesPerStaffCombo : maxPiecesPerStaff;
      await apiClient.post('/wms/dispatch/pieces-processing/settings', {
        max_pieces_per_staff: value,
        processing_type: processingType
      });
      const label = processingType === 'combo' ? 'Combo' : 'Regular';
      toast.success(`${label} max pieces per staff set to ${value}`);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSetPriority = async () => {
    if (!selectedSlip) return;
    
    try {
      await apiClient.post(`/wms/dispatch/set-priority/${selectedSlip.slip_id}`, {
        priority: parseInt(newPriority)
      });
      toast.success('Priority updated');
      setPriorityDialogOpen(false);
      fetchPendingSlips();
    } catch (error) {
      toast.error('Failed to set priority');
    }
  };

  const handleAssignPicker = async () => {
    if (!selectedSlip || !selectedStaffForAssign) return;
    
    try {
      await apiClient.post('/wms/dispatch/assign-picker', {
        slip_id: selectedSlip.slip_id,
        staff_id: selectedStaffForAssign
      });
      toast.success('Picker assigned successfully');
      setAssignDialogOpen(false);
      setSelectedStaffForAssign('');
      fetchPendingSlips();
      fetchPickerStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign picker');
    }
  };

  const getPriorityBadge = (priority) => {
    const configs = {
      1: { label: 'Highest', variant: 'destructive' },
      2: { label: 'High', variant: 'default' },
      3: { label: 'Normal', variant: 'secondary' },
      4: { label: 'Low', variant: 'outline' }
    };
    const config = configs[priority] || configs[3];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const configs = {
      'PENDING': { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      'IMPORTED': { label: 'Imported', className: 'bg-yellow-100 text-yellow-800' },
      'ASSIGNED': { label: 'Assigned', className: 'bg-blue-100 text-blue-800' },
      'IN_PROGRESS': { label: 'In Progress', className: 'bg-purple-100 text-purple-800' },
      'COMPLETED': { label: 'Completed', className: 'bg-green-100 text-green-800' },
      'PACKED': { label: 'Packed', className: 'bg-emerald-100 text-emerald-800' }
    };
    const config = configs[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${config.className}`}>{config.label}</span>;
  };

  // Get all available pickers for assignment dropdown
  const allAvailablePickers = [...sfgDhotiPickers, ...sfgComboPickers, ...fgPickers].filter(
    (picker, index, self) => index === self.findIndex(p => p.staff_id === picker.staff_id)
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">Dispatch Supervisor</h1>
                <p className="text-sm text-[#E8DCC8]">Manage pickers & pick slip allocation</p>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => {
            fetchAvailablePickers();
            fetchPendingSlips();
            fetchPickerStats();
          }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => navigate('/wms/staff-rankings')}
            className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
            data-testid="staff-rankings-btn"
          >
            <Trophy className="w-4 h-4 mr-2" />
            Staff Rankings
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="pickers">Available Pickers</TabsTrigger>
            <TabsTrigger value="slips">Pick Slips</TabsTrigger>
            <TabsTrigger value="stats">Picker Stats</TabsTrigger>
          </TabsList>

          {/* Pickers Tab */}
          <TabsContent value="pickers">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Dhoti Pickers (SFG, trigger_type: MANUAL_CASCADING / MANUAL / AUTO_REPLENISHMENT) */}
              <Card data-testid="dhoti-pickers-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-indigo-500" />
                    Dhoti Pickers
                  </CardTitle>
                  <CardDescription>Select staff for Dhoti SFG picking (Manual / Cascading / Replenishment)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search staff..."
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                      data-testid="dhoti-pickers-search"
                    />
                    <Button variant="outline" onClick={handleSearchStaff}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Staff list with checkboxes */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {staffList.map((staff) => (
                      <div
                        key={staff.staff_id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <Checkbox
                          checked={selectedSfgDhotiPickers.includes(staff.staff_id)}
                          onCheckedChange={() => handleTogglePicker(staff.staff_id, 'SFG_DHOTI')}
                          data-testid={`dhoti-picker-checkbox-${staff.staff_id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                        </div>
                      </div>
                    ))}
                    {staffList.length === 0 && (
                      <p className="p-4 text-center text-muted-foreground">No staff found</p>
                    )}
                  </div>

                  {/* Selected count and save */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground" data-testid="dhoti-pickers-count">
                      {selectedSfgDhotiPickers.length} picker(s) selected
                    </span>
                    <Button onClick={() => handleSavePickers('SFG_DHOTI')} data-testid="save-dhoti-pickers-btn">
                      Save Dhoti Pickers
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Combo Pickers (SFG, trigger_type: COMBO_V2_BULK / COMBO_ASSEMBLY) */}
              <Card data-testid="combo-pickers-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-fuchsia-500" />
                    Combo Pickers
                  </CardTitle>
                  <CardDescription>Select staff for Combo SFG picking (Combo V2 Bulk / Combo Assembly)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search staff..."
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                      data-testid="combo-pickers-search"
                    />
                    <Button variant="outline" onClick={handleSearchStaff}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Staff list with checkboxes */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {staffList.map((staff) => (
                      <div
                        key={staff.staff_id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <Checkbox
                          checked={selectedSfgComboPickers.includes(staff.staff_id)}
                          onCheckedChange={() => handleTogglePicker(staff.staff_id, 'SFG_COMBO')}
                          data-testid={`combo-picker-checkbox-${staff.staff_id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                        </div>
                      </div>
                    ))}
                    {staffList.length === 0 && (
                      <p className="p-4 text-center text-muted-foreground">No staff found</p>
                    )}
                  </div>

                  {/* Selected count and save */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground" data-testid="combo-pickers-count">
                      {selectedSfgComboPickers.length} picker(s) selected
                    </span>
                    <Button onClick={() => handleSavePickers('SFG_COMBO')} data-testid="save-combo-pickers-btn">
                      Save Combo Pickers
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* FG Pickers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-emerald-500" />
                    FG Pickers
                  </CardTitle>
                  <CardDescription>Select staff available for FG picking today</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search staff..."
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                    />
                    <Button variant="outline" onClick={handleSearchStaff}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Staff list with checkboxes */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {staffList.map((staff) => (
                      <div
                        key={staff.staff_id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <Checkbox
                          checked={selectedFgPickers.includes(staff.staff_id)}
                          onCheckedChange={() => handleTogglePicker(staff.staff_id, 'FG')}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                        </div>
                      </div>
                    ))}
                    {staffList.length === 0 && (
                      <p className="p-4 text-center text-muted-foreground">No staff found</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedFgPickers.length} picker(s) selected
                    </span>
                    <Button onClick={() => handleSavePickers('FG')}>
                      Save FG Pickers
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Regular Pieces Processing Staff */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-amber-500" />
                    Regular Processing Staff
                  </CardTitle>
                  <CardDescription>
                    Staff for standard dispatch pick slips (non-combo)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Max pieces setting */}
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Label className="whitespace-nowrap text-sm">Max pieces:</Label>
                    <Input
                      type="number"
                      value={maxPiecesPerStaff}
                      onChange={(e) => setMaxPiecesPerStaff(parseInt(e.target.value) || 100)}
                      className="w-20"
                      min={1}
                      max={1000}
                      data-testid="max-pieces-regular-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => handleSaveMaxPieces('regular')} data-testid="save-limit-regular-btn">
                      Save
                    </Button>
                  </div>

                  {/* Search */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search staff..."
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                    />
                    <Button variant="outline" onClick={handleSearchStaff}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Staff list with checkboxes */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {staffList.map((staff) => (
                      <div
                        key={staff.staff_id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <Checkbox
                          checked={selectedPiecesProcessingStaff.includes(staff.staff_id)}
                          onCheckedChange={() => handleTogglePicker(staff.staff_id, 'PIECES_PROCESSING')}
                          data-testid={`regular-pp-checkbox-${staff.staff_id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                        </div>
                      </div>
                    ))}
                    {staffList.length === 0 && (
                      <p className="p-4 text-center text-muted-foreground">No staff found</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedPiecesProcessingStaff.length} staff selected
                    </span>
                    <Button onClick={() => handleSavePickers('PIECES_PROCESSING')} className="bg-amber-600 hover:bg-amber-700" data-testid="save-regular-pp-btn">
                      Save Regular Staff
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Combo Pieces Processing Staff */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-500" />
                    Combo Processing Staff
                  </CardTitle>
                  <CardDescription>
                    Staff for combo assembly pick slips only
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Max pieces setting */}
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <Label className="whitespace-nowrap text-sm">Max pieces:</Label>
                    <Input
                      type="number"
                      value={maxPiecesPerStaffCombo}
                      onChange={(e) => setMaxPiecesPerStaffCombo(parseInt(e.target.value) || 100)}
                      className="w-20"
                      min={1}
                      max={1000}
                      data-testid="max-pieces-combo-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => handleSaveMaxPieces('combo')} data-testid="save-limit-combo-btn">
                      Save
                    </Button>
                  </div>

                  {/* Search */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search staff..."
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                    />
                    <Button variant="outline" onClick={handleSearchStaff}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Staff list with checkboxes */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {staffList.map((staff) => (
                      <div
                        key={staff.staff_id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <Checkbox
                          checked={selectedComboProcessingStaff.includes(staff.staff_id)}
                          onCheckedChange={() => handleTogglePicker(staff.staff_id, 'PIECES_PROCESSING_COMBO')}
                          data-testid={`combo-pp-checkbox-${staff.staff_id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                        </div>
                      </div>
                    ))}
                    {staffList.length === 0 && (
                      <p className="p-4 text-center text-muted-foreground">No staff found</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedComboProcessingStaff.length} staff selected
                    </span>
                    <Button onClick={() => handleSavePickers('PIECES_PROCESSING_COMBO')} className="bg-purple-600 hover:bg-purple-700" data-testid="save-combo-pp-btn">
                      Save Combo Staff
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Packers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-500" />
                    Packers
                  </CardTitle>
                  <CardDescription>
                    Staff for packing boxes at FG Packing Station
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search staff..."
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                    />
                    <Button variant="outline" onClick={handleSearchStaff}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Staff list with checkboxes */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {staffList.map((staff) => (
                      <div
                        key={staff.staff_id}
                        className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <Checkbox
                          checked={selectedPackers.includes(staff.staff_id)}
                          onCheckedChange={() => handleTogglePicker(staff.staff_id, 'PACKER')}
                          data-testid={`packer-checkbox-${staff.staff_id}`}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                        </div>
                      </div>
                    ))}
                    {staffList.length === 0 && (
                      <p className="p-4 text-center text-muted-foreground">No staff found</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedPackers.length} staff selected
                    </span>
                    <Button onClick={() => handleSavePickers('PACKER')} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-packers-btn">
                      Save Packers
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Packer Groups (persistent — credited equally at packing) */}
              <Card data-testid="packer-groups-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-600" />
                    Packer Groups
                  </CardTitle>
                  <CardDescription>
                    Create named groups of packers. At the Packing Station you pick a group and the box pieces are credited equally to all members.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Existing groups */}
                  <div className="space-y-2">
                    {packerGroups.length === 0 && (
                      <p className="text-sm text-muted-foreground">No groups yet. Create one below.</p>
                    )}
                    {packerGroups.map((g) => (
                      <div key={g.group_id} className="border rounded-lg p-3 flex items-start justify-between gap-2" data-testid={`packer-group-${g.group_id}`}>
                        <div className="flex-1">
                          <p className="font-semibold text-teal-700">{g.group_name}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(g.members || []).map(m => (
                              <Badge key={m.staff_id} variant="outline" className="bg-teal-50 text-xs">{m.staff_name || m.staff_id}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" onClick={() => handleEditGroup(g)} data-testid={`edit-group-${g.group_id}`}>Edit</Button>
                          <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteGroup(g)} data-testid={`delete-group-${g.group_id}`}>Delete</Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Create / edit form */}
                  <div className="border-t pt-4 space-y-3">
                    <p className="text-sm font-medium">{editingGroupId ? 'Edit Group' : 'Create New Group'}</p>
                    <Input
                      placeholder="Group name (e.g. Packing Team A)"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      data-testid="group-name-input"
                    />
                    <Input
                      placeholder="Search staff to add..."
                      value={groupStaffSearch}
                      onChange={(e) => setGroupStaffSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchStaff()}
                      data-testid="group-staff-search"
                    />
                    <div className="border rounded-lg max-h-48 overflow-y-auto">
                      {staffList.map((staff) => (
                        <div key={staff.staff_id} className="flex items-center gap-3 p-2 border-b last:border-b-0 hover:bg-gray-50">
                          <Checkbox
                            checked={groupMemberIds.some(m => m.staff_id === staff.staff_id)}
                            onCheckedChange={() => toggleGroupMember(staff)}
                            data-testid={`group-member-checkbox-${staff.staff_id}`}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{staff.staff_name}</p>
                            <p className="text-xs text-muted-foreground">{staff.staff_id}</p>
                          </div>
                        </div>
                      ))}
                      {staffList.length === 0 && (
                        <p className="p-3 text-center text-muted-foreground text-sm">No staff found</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{groupMemberIds.length} member(s)</span>
                      <div className="flex gap-2">
                        {editingGroupId && (
                          <Button variant="outline" onClick={resetGroupForm} data-testid="cancel-group-edit-btn">Cancel</Button>
                        )}
                        <Button onClick={handleSaveGroup} disabled={savingGroup} className="bg-teal-600 hover:bg-teal-700" data-testid="save-group-btn">
                          {editingGroupId ? 'Update Group' : 'Create Group'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Current Available Pickers Summary */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Today's Available Staff</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <h4 className="font-medium mb-2 text-indigo-600">Dhoti Pickers ({sfgDhotiPickers.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {sfgDhotiPickers.map(p => (
                        <Badge key={p.staff_id} variant="outline" className="bg-indigo-50">
                          {p.staff_name}
                          {p.status === 'busy' && <span className="ml-1 text-orange-500">●</span>}
                        </Badge>
                      ))}
                      {sfgDhotiPickers.length === 0 && <span className="text-muted-foreground text-sm">None selected</span>}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-fuchsia-600">Combo Pickers ({sfgComboPickers.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {sfgComboPickers.map(p => (
                        <Badge key={p.staff_id} variant="outline" className="bg-fuchsia-50">
                          {p.staff_name}
                          {p.status === 'busy' && <span className="ml-1 text-orange-500">●</span>}
                        </Badge>
                      ))}
                      {sfgComboPickers.length === 0 && <span className="text-muted-foreground text-sm">None selected</span>}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-emerald-600">FG Pickers ({fgPickers.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {fgPickers.map(p => (
                        <Badge key={p.staff_id} variant="outline" className="bg-emerald-50">
                          {p.staff_name}
                          {p.status === 'busy' && <span className="ml-1 text-orange-500">●</span>}
                        </Badge>
                      ))}
                      {fgPickers.length === 0 && <span className="text-muted-foreground text-sm">None selected</span>}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-amber-600">Regular Processing ({piecesProcessingStaff.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {piecesProcessingStaff.map(p => (
                        <Badge key={p.staff_id} variant="outline" className="bg-amber-50">
                          {p.staff_name}
                        </Badge>
                      ))}
                      {piecesProcessingStaff.length === 0 && <span className="text-muted-foreground text-sm">None selected</span>}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-purple-600">Combo Processing ({comboProcessingStaff.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {comboProcessingStaff.map(p => (
                        <Badge key={p.staff_id} variant="outline" className="bg-purple-50">
                          {p.staff_name}
                        </Badge>
                      ))}
                      {comboProcessingStaff.length === 0 && <span className="text-muted-foreground text-sm">None selected</span>}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-cyan-600">Packers ({packers.length})</h4>
                    <div className="flex flex-wrap gap-2">
                      {packers.map(p => (
                        <Badge key={p.staff_id} variant="outline" className="bg-cyan-50">
                          {p.staff_name}
                        </Badge>
                      ))}
                      {packers.length === 0 && <span className="text-muted-foreground text-sm">None selected</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pick Slips Tab */}
          <TabsContent value="slips">
            <Card>
              <CardHeader>
                <CardTitle>Pending Pick Slips</CardTitle>
                <CardDescription>Set priority and assign pickers to slips</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingSlips ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin" />
                  </div>
                ) : pendingSlips.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No pending pick slips
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Slip No</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>SKU/Items</TableHead>
                          <TableHead>Customer/Design</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Priority</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Assigned To</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingSlips.map((slip) => (
                          <TableRow key={slip.slip_id || slip.slip_no}>
                            <TableCell className="font-mono">{slip.slip_no}</TableCell>
                            <TableCell>
                              <Badge variant={slip.slip_type === 'FG' ? 'default' : slip.slip_type === 'FG_TRANSFER' ? 'outline' : 'secondary'} 
                                     className={slip.slip_type === 'FG_TRANSFER' ? 'border-purple-500 text-purple-700' : ''}>
                                {slip.slip_type === 'FG_TRANSFER' ? 'FG Transfer' : (slip.slip_type || 'SFG')}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{slip.sku_code}</TableCell>
                            <TableCell>{slip.design_name || '-'}</TableCell>
                            <TableCell>{slip.pick_qty} pcs</TableCell>
                            <TableCell>{getPriorityBadge(slip.priority || 3)}</TableCell>
                            <TableCell>{getStatusBadge(slip.status)}</TableCell>
                            <TableCell>{slip.assigned_to_name || '-'}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedSlip(slip);
                                    setNewPriority(String(slip.priority || 3));
                                    setPriorityDialogOpen(true);
                                  }}
                                >
                                  <Flag className="w-3 h-3" />
                                </Button>
                                {!slip.assigned_to && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedSlip(slip);
                                      setAssignDialogOpen(true);
                                    }}
                                  >
                                    <UserPlus className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle>Picker Performance - Today</CardTitle>
              </CardHeader>
              <CardContent>
                {pickerStats.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No picker activity today</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Picker</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Current Slip</TableHead>
                          <TableHead>Slips Completed</TableHead>
                          <TableHead>Total Picks</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pickerStats.map((stat, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{stat.staff_name || stat.staff_id}</TableCell>
                            <TableCell>
                              <Badge variant={stat.picker_type === 'SFG' ? 'default' : 'secondary'}>
                                {stat.picker_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                stat.status === 'available' ? 'bg-green-100 text-green-800' :
                                stat.status === 'busy' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {stat.status || 'available'}
                              </span>
                            </TableCell>
                            <TableCell>{stat.current_slip || '-'}</TableCell>
                            <TableCell>{stat.slips_completed || 0}</TableCell>
                            <TableCell>{stat.total_picks || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Priority Dialog */}
      <Dialog open={priorityDialogOpen} onOpenChange={setPriorityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Priority</DialogTitle>
            <DialogDescription>
              Set priority for slip {selectedSlip?.slip_no}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Priority Level</Label>
            <Select value={newPriority} onValueChange={setNewPriority}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 - Highest (Urgent)</SelectItem>
                <SelectItem value="2">2 - High</SelectItem>
                <SelectItem value="3">3 - Normal</SelectItem>
                <SelectItem value="4">4 - Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriorityDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSetPriority}>Save Priority</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Picker Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Picker</DialogTitle>
            <DialogDescription>
              Assign slip {selectedSlip?.slip_no} to a picker
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Picker</Label>
            <Select value={selectedStaffForAssign} onValueChange={setSelectedStaffForAssign}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a picker..." />
              </SelectTrigger>
              <SelectContent>
                {allAvailablePickers.map(picker => (
                  <SelectItem 
                    key={picker.staff_id} 
                    value={picker.staff_id}
                    disabled={picker.status === 'busy'}
                  >
                    {picker.staff_name} {picker.status === 'busy' && '(Busy)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignPicker} disabled={!selectedStaffForAssign}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DispatchSupervisor;
